import type { Block } from '../composition/model.ts'
import { APP_SETTINGS } from '../../config/appSettings.ts'
import type { Note } from '../../domain/note.ts'

export const MANUAL_SECTION = 'explicit-notes'
export const LAB = APP_SETTINGS.intelligence.contextLab
export const LAYOUTS = ['brief', 'cards', 'comparison', 'sequence', 'list', 'checklist'] as const
export type Layout = typeof LAYOUTS[number]
export const CONTRIBUTION_KINDS = ['fact', 'person', 'place', 'activity', 'task', 'question', 'notice', 'option', 'summary'] as const
export type ContributionKind = typeof CONTRIBUTION_KINDS[number]
export type SourceRef = { id: string; revision: string }
export interface Contribution {
  id: string
  /** Validated component; embedded sources use durable note IDs, never packet aliases. */
  block?: Block
  knowledgeId?: string
  evidence?: { sourceId: string; quote: string }[]
  kind: ContributionKind
  title: string
  text: string
  sources: SourceRef[]
  basis: 'source' | 'interpretation' | 'proposal'
  fit: 'placed' | 'maybe'
  origin: 'manual' | 'model'
  whyUseful?: string
  facts: { label: string; value: string }[]
  time?: string
  certainty?: 'tentative' | 'unclear'
  completed?: boolean
}
export interface LensSection {
  id: string
  title: string
  layout: Layout
  alternateLayouts?: Layout[]
  layoutReason?: string
  contributions: Contribution[]
}
export interface Lens { id: string; title: string; purpose: string; sections: LensSection[] }
export interface ExperimentVariant {
  scenarioId: string
  scenarioTitle: string
  id: string
  label: string
  order: number
}
export interface Context { id: string; aliases?: string[]; title: string; purpose: string; lenses: Lens[]; experiment?: ExperimentVariant }
export interface ReviewPacket { version: 1; label: string; contexts: Context[] }

export function createSeedPacket(): ReviewPacket {
  const seeds = [
    ['israel-oct-2026', 'Israel · October 2026', 'Plan the trip and bring useful details together.', ['Overview', 'Stay options', 'Arrival']],
    ['car-leasing', 'Car leasing', 'Compare options and work toward a decision.', ['Overview', 'Compare options']],
    ['dinner-sept-2026', 'Dinner party · late September 2026', 'Bring together people, food and preparations.', ['Overview', 'Menu ideas', 'Preparations']],
  ] as const
  return {
    version: 1,
    label: 'Owner-selected contexts · starter Lenses, not AI discoveries',
    contexts: seeds.map(([id, title, purpose, lenses]) => ({
      id, title, purpose,
      lenses: lenses.map((title, index) => ({
        id: `${id}-${index}`, title, purpose: `Useful material for ${title.toLowerCase()}.`,
        sections: [{ id: `${id}-${index}-main`, title: 'Useful bits', layout: 'cards', contributions: [] }],
      })),
    })),
  }
}

export function cohortNotes(notes: Note[], capturedIds: string[] = []): Note[] {
  const captured = new Set(capturedIds)
  return notes.filter((note) => note.status !== 'trash' && (
    captured.has(note.id) || note.userTags.some((tag) => tag.replace(/^#/, '').toLowerCase() === LAB.cohortTag)
  ))
}

// Explicit, local preparation only. Full source text; no account/device fields,
// AI tags or captures outside the owner-selected cohort. Aliases are packet-local.
export function prepareSourcePacket(notes: Note[]) {
  return {
    version: 1,
    cohort: LAB.cohortTag,
    sources: cohortNotes(notes).map((note, index) => ({
      alias: `N${String(index + 1).padStart(2, '0')}`,
      ref: { id: note.id, revision: note.revisionId },
      title: note.manualTitle || note.derivedTitle || 'Untitled capture',
      titleOrigin: note.manualTitle ? 'stored-title' : 'derived',
      body: note.body,
      annotation: note.annotation,
      createdAt: note.createdAt,
      modifiedAt: note.modifiedAt,
    })),
  }
}

export function sourceState(item: Contribution, notes: Map<string, Note>): 'current' | 'changed' | 'unavailable' {
  if (item.sources.some((ref) => !notes.has(ref.id) || notes.get(ref.id)?.status === 'trash')) return 'unavailable'
  return item.sources.some((ref) => notes.get(ref.id)?.revisionId !== ref.revision) ? 'changed' : 'current'
}

export function placeNote(packet: ReviewPacket, contextId: string, lensId: string, note: Note): ReviewPacket {
  return { ...packet, contexts: packet.contexts.map((context) => context.id !== contextId ? context : {
    ...context, lenses: context.lenses.map((lens) => lens.id !== lensId ? lens : {
      ...lens, sections: lens.sections.map((section, index) => index !== 0
        || lens.sections.some((s) => s.contributions.some((item) => item.id === `manual-${note.id}`))
        ? section : {
          ...section, contributions: [...section.contributions, {
            id: `manual-${note.id}`, title: note.manualTitle || note.derivedTitle || 'Untitled capture',
            text: note.body.slice(0, LAB.sourcePreviewChars),
            sources: [{ id: note.id, revision: note.revisionId }],
            kind: 'fact', basis: 'source', fit: 'placed', origin: 'manual', facts: [],
          } satisfies Contribution].slice(0, LAB.maxContributions),
        }),
    }),
  }) }
}

// Deliberately a small viewer contract, not the production inference schema.
// Reconstruct known fields: never spread untrusted model JSON into component props.
export function parseReviewPacket(text: string, allowEmpty = false): ReviewPacket {
  if (text.length > LAB.maxPacketChars) throw new Error('Review packet is too large.')
  const fail = (): never => { throw new Error('Invalid review packet. Use an exported viewer packet as the template.') }
  const object = (value: unknown): Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : fail()
  const string = (value: unknown, max = LAB.maxLabelChars): string =>
    typeof value === 'string' && value.trim().length > 0 && value.length <= max ? value : fail()
  const optionalText = (value: unknown): string => value === '' ? '' : string(value, LAB.maxTextChars)
  const list = (value: unknown, max: number): unknown[] => Array.isArray(value) && value.length <= max ? value : fail()
  const choice = <T extends string>(value: unknown, values: readonly T[]): T =>
    values.includes(value as T) ? value as T : fail()
  const unique = <T extends { id: string }>(items: T[]): T[] => new Set(items.map((item) => item.id)).size === items.length ? items : fail()
  const packet = object(JSON.parse(text))
  if (packet.version !== 1) fail()
  const contexts = unique(list(packet.contexts, LAB.maxContexts).map((raw): Context => {
    const c = object(raw)
    const lenses = unique(list(c.lenses, LAB.maxLenses).map((raw): Lens => {
      const l = object(raw)
      const sections = unique(list(l.sections, LAB.maxSections).map((raw): LensSection => {
        const s = object(raw)
        const layout = choice(s.layout, LAYOUTS)
        const alternateLayouts = s.alternateLayouts === undefined ? undefined
          : list(s.alternateLayouts, LAB.maxAlternateLayouts).map((value) => choice(value, LAYOUTS))
        if (alternateLayouts && (new Set(alternateLayouts).size !== alternateLayouts.length || alternateLayouts.includes(layout))) fail()
        return { id: string(s.id), title: string(s.title), layout,
          ...(alternateLayouts ? { alternateLayouts } : {}),
          ...(s.layoutReason === undefined ? {} : { layoutReason: string(s.layoutReason, LAB.maxTextChars) }),
          contributions: unique(list(s.contributions, LAB.maxContributions).map((raw): Contribution => {
            const item = object(raw)
            const sources = list(item.sources, LAB.maxSources).map((raw) => {
              const ref = object(raw)
              return { id: string(ref.id), revision: string(ref.revision) }
            })
            if (!sources.length) fail()
            return { id: string(item.id), kind: item.kind === undefined ? 'fact' : choice(item.kind, CONTRIBUTION_KINDS),
              title: string(item.title), text: optionalText(item.text), sources,
              basis: choice(item.basis, ['source', 'interpretation', 'proposal']),
              fit: choice(item.fit, ['placed', 'maybe']), origin: choice(item.origin, ['manual', 'model']),
              ...(item.whyUseful === undefined ? {} : { whyUseful: string(item.whyUseful, LAB.maxTextChars) }),
              facts: list(item.facts, LAB.maxFacts).map((raw) => {
                const fact = object(raw)
                return { label: string(fact.label), value: string(fact.value) }
              }), ...(item.time === undefined ? {} : { time: string(item.time) }),
              ...(item.certainty === undefined ? {} : { certainty: choice(item.certainty, ['tentative', 'unclear'] as const) }),
              ...(item.completed === undefined ? {} : typeof item.completed === 'boolean' ? { completed: item.completed } : fail()),
            }
          })),
        }
      }))
      if (!sections.length) fail()
      return { id: string(l.id), title: string(l.title), purpose: string(l.purpose), sections }
    }))
    if (!lenses.length) fail()
    let experiment: ExperimentVariant | undefined
    if (c.experiment !== undefined) {
      const raw = object(c.experiment)
      if (!Number.isInteger(raw.order) || Number(raw.order) < 0 || Number(raw.order) > 99) fail()
      experiment = { scenarioId: string(raw.scenarioId), scenarioTitle: string(raw.scenarioTitle),
        id: string(raw.id), label: string(raw.label), order: Number(raw.order) }
    }
    return { id: string(c.id), ...(c.aliases === undefined ? {} : { aliases: list(c.aliases, LAB.maxContexts).map(alias => string(alias)) }), title: string(c.title), purpose: string(c.purpose), lenses,
      ...(experiment ? { experiment } : {}) }
  }))
  if (!contexts.length && !allowEmpty) fail()
  return { version: 1, label: string(packet.label), contexts }
}
